/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { MetadataElement } from '../../../services/api/files'

const ReadOnlyValueStyledDiv = styled.div<{ $disabled?: boolean; $inherited?: boolean }>`
  padding-left: 4px;
  color: ${(props) => (props.$disabled ? '#B7B7B7' : '#000')};
  font-style: ${(props) => (props.$inherited ? 'italic' : 'normal')};
`

interface PropertiesInfo {
  isMultiFile?: boolean
  isTemplate?: boolean
  [key: string]: any
}

interface FsiPropertiesInlineEditorProps {
  isAclFlow?: boolean
  input: React.ReactNode
  value?: any | (() => any)
  formatter?: (value: any) => string
  property: MetadataElement
  propertyCopy?: MetadataElement
  propertiesInfo?: PropertiesInfo
  isEditable: boolean
  isRequired?: boolean
  isRevertDisabled?: boolean
  onAccept: (property: MetadataElement) => void
  onRevert: (property: MetadataElement) => void
  onCancel: (property: MetadataElement) => void
}

export function FsiPropertiesInlineEditor({
  isAclFlow = false,
  input,
  value,
  formatter,
  property,
  propertiesInfo,
  isEditable,
  isRevertDisabled,
  onAccept,
  onRevert,
  onCancel
}: FsiPropertiesInlineEditorProps) {
  const { t } = useTranslation()
  const [inEditMode, setInEditMode] = useState(false)
  const [displayValue, setDisplayValue] = useState<string>('')
  const containerRef = useRef<any>(null)

  useEffect(() => {
    const onClickEvent = (event: any) => {
      const element = containerRef.current?.current || containerRef.current
      if (!element || !inEditMode) return
      // 1. Check if the clicked element is still in the document
      const isStillInDom = document.body.contains(event.target)
      // 2. Check if the click was inside our container
      const isInside = element.contains(event.target)
      // ONLY close if the click is still in the DOM AND it's outside our ref
      if (isStillInDom && !isInside) {
        setInEditMode(false)
      }
    }
    if (inEditMode) {
      document.body.addEventListener('click', onClickEvent)
    }

    return () => {
      document.body.removeEventListener('click', onClickEvent)
    }
  }, [inEditMode])

  // Update display value when property changes
  useEffect(() => {
    formatValue()
  }, [property, value])

  function formatValue() {
    const val = typeof value === 'function' ? value() : property.value

    if (!isAclFlow) {
      let formattedValue: string

      if (formatter !== undefined) {
        formattedValue = formatter(val)
      } else if (Array.isArray(val) && val.length > 0) {
        // Array of values - join with comma
        formattedValue = val.join(', ')
      } else if (val === null || val === undefined || (Array.isArray(val) && val.length === 0)) {
        // Empty value
        if ((propertiesInfo?.isMultiFile && property.mixedValue) || (property as any)?.fromArtifact?.mixedValue) {
          formattedValue = t('PROPERTIES_MIXED_VALUES')
        } else {
          formattedValue = '—'
        }
      } else {
        formattedValue = String(val)
      }

      setDisplayValue(formattedValue)
    } else {
      // ACL flow
      const aclValue = Array.isArray(val) && val.length ? val.map((item: any) => item?.label).join(', ') : t('ACL_NONE')
      setDisplayValue(aclValue)
    }
  }

  const isReadOnly = (!isAclFlow && property?.template?.readOnly && !propertiesInfo?.isTemplate) || !isEditable

  return (
    <div title={displayValue} ref={containerRef}>
      {isReadOnly ? (
        <ReadOnlyValueStyledDiv
          $disabled={property?.template?.readOnly || !isEditable}
          $inherited={property?.inherited}
          className={isAclFlow ? 'acl-level-data fs12' : ''}
        >
          {displayValue}
        </ReadOnlyValueStyledDiv>
      ) : inEditMode ? (
        <>
          <div>{input}</div>
          <div className="edit-button-group2 pull-right">
            {!isAclFlow && (
              <button
                data-action="reset"
                title={t('PROPERTIES_BTN_TITLE_REVERT')}
                className="btn_cstm"
                disabled={property?.inherited || property?.revertToInherited || isRevertDisabled}
                onClick={(e) => {
                  e.stopPropagation()
                  onRevert(property)
                }}
              >
                <span className="return_icon"></span>
              </button>
            )}
            <button
              data-action="save"
              title={t('PROPERTIES_BTN_TITLE_SUBMIT')}
              className="btn_cstm"
              onClick={() => {
                setInEditMode(false)
                onAccept(property)
              }}
            >
              <span className="save_icon"></span>
            </button>
            <button
              data-action="cancel"
              title={t('PROPERTIES_BTN_TITLE_CANCEL')}
              className="btn_cstm"
              onClick={() => {
                setInEditMode(false)
                onCancel(property)
              }}
            >
              <span className="cancel_icon"></span>
            </button>
          </div>
        </>
      ) : (
        <span
          className={'fake_input' + (property?.inherited ? ' inherited' : '')}
          onClick={(e) => {
            e.stopPropagation()
            setInEditMode(true)
          }}
        >
          {displayValue}
          <span
            title={t('PROPERTIES_BTN_TITLE_CLICK_TO_EDIT')}
            className="glyphicon2"
            aria-hidden="true"
            onClick={(e) => {
              e.stopPropagation()
              setInEditMode(true)
            }}
          ></span>
        </span>
      )}
    </div>
  )
}

export default FsiPropertiesInlineEditor
